// ============================================================================
// ENUMS
// ============================================================================

export enum CatalogPlanCode {
  FREE = 'CATALOGUE_FREE',
  PRO = 'CATALOGUE_PRO',
  PREMIUM = 'CATALOGUE_PREMIUM',
}

export enum CatalogPlanStatus {
  ACTIVE = 'ACTIVE',
  TRIALING = 'TRIALING',
  PAST_DUE = 'PAST_DUE',
  CANCELLED = 'CANCELLED',
  EXPIRED = 'EXPIRED',
}

// ============================================================================
// INTERFACES
// ============================================================================

export interface MerchantCatalogPlan {
  id: string;
  digitalCardId: string;
  planCode: CatalogPlanCode;
  status: CatalogPlanStatus;
  isTrialActive: boolean;
  trialStartedAt?: string | null;
  trialEndsAt?: string | null;
  currentPeriodStart?: string | null;
  currentPeriodEnd?: string | null;
  cancelAtPeriodEnd: boolean;
  canceledAt?: string | null;
  billingCountry: string;
  billingCurrency: string;
  referralsEarned: number;
  referralBonusProducts: number;
  hasReferralPhotoBonus: boolean;
  createdAt: string;
  updatedAt: string;
  // Relations
  digitalCard?: {
    id: string;
    displayName?: string | null;
    slug?: string | null;
    cardType?: string | null;
    user?: {
      id: string;
      firstName?: string | null;
      lastName?: string | null;
      email?: string | null;
      phoneNumber?: string | null;
    } | null;
  } | null;
  referralCode?: {
    code: string;
    totalClicks: number;
    totalSignups: number;
    totalEarned: number;
    referrals?: Array<{
      id: string;
      status: string;
      createdAt: string;
      rewardedAt?: string | null;
      referred?: {
        id: string;
        firstName?: string | null;
        lastName?: string | null;
        email?: string | null;
      } | null;
    }>;
  } | null;
}

export interface MerchantCatalogPlanConnection {
  items: MerchantCatalogPlan[];
  total: number;
  page: number;
  pageSize: number;
}

export interface MerchantCatalogPlanStats {
  total: number;
  byStatus: Record<string, number>;
  byPlanCode: Record<string, number>;
  activeTrials: number;
  trialConversionRate: number;
}

// ============================================================================
// HELPERS
// ============================================================================

export const PLAN_LABELS: Record<string, string> = {
  CATALOGUE_FREE: 'Gratuit',
  CATALOGUE_PRO: 'Pro',
  CATALOGUE_PREMIUM: 'Premium',
};

export const PLAN_COLORS: Record<string, 'default' | 'primary' | 'warning' | 'success' | 'error' | 'info' | 'secondary'> = {
  CATALOGUE_FREE: 'default',
  CATALOGUE_PRO: 'primary',
  CATALOGUE_PREMIUM: 'success',
};

export const STATUS_LABELS: Record<string, string> = {
  ACTIVE: 'Actif',
  TRIALING: 'En essai',
  PAST_DUE: 'Paiement en retard',
  CANCELLED: 'Annulé',
  EXPIRED: 'Expiré',
};

export const STATUS_COLORS: Record<string, 'default' | 'primary' | 'warning' | 'success' | 'error' | 'info' | 'secondary'> = {
  ACTIVE: 'success',
  TRIALING: 'info',
  PAST_DUE: 'warning',
  CANCELLED: 'default',
  EXPIRED: 'error',
};

export function formatDate(date?: string | null): string {
  if (!date) return '—';
  return new Date(date).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
}

export function formatDateTime(date?: string | null): string {
  if (!date) return '—';
  return new Date(date).toLocaleString('fr-FR', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

export function trialDaysRemaining(trialEndsAt?: string | null): number {
  if (!trialEndsAt) return 0;
  return Math.max(0, Math.ceil((new Date(trialEndsAt).getTime() - Date.now()) / 86_400_000));
}
